import { getGeolocationPermissionState, type GeoPermissionState } from "@/lib/geolocationPermission";

/**
 * Browse's quiet "where are you now" refresh. Onboarding's "Where are you
 * based?" is the only place that asks for location, so this refresh must never
 * be what makes the location pop up appear, and it must never replace an area
 * the person typed or picked.
 */

type Shown = { lat: number; lng: number; description?: string } | null | undefined;

export function isCurrentLocation(shown: Shown): boolean {
  return !!shown && shown.description === "Current Location";
}

/**
 * True when asking the device for its position cannot show a pop up the
 * person never agreed to: access is already granted, or Browse is already
 * showing their "Current Location" (they chose GPS before).
 */
export function mayQuietlyRefreshLocation(permission: GeoPermissionState, shown: Shown): boolean {
  if (permission === "denied") return false;
  if (permission === "granted") return true;
  return isCurrentLocation(shown);
}

/** Only an existing "Current Location" moves, and only past 0.12 mi. */
export function shouldMoveCurrentLocation(shown: Shown, movedMiles: number | null): boolean {
  if (!isCurrentLocation(shown)) return false;
  return movedMiles != null && movedMiles >= 0.12;
}

/** The location Browse last showed, as saved on this device. */
export function readShownBrowseLocation(): Shown {
  try {
    const raw = localStorage.getItem("grounded_browse_location");
    if (!raw) return null;
    const loc = JSON.parse(raw);
    return typeof loc?.lat === "number" && typeof loc?.lng === "number" ? loc : null;
  } catch {
    return null;
  }
}

/**
 * For checks that run on their own (not from a tap), like the nearby check in
 * prompt: true only when asking the device for its position cannot show the
 * location pop up to someone who typed an area or tapped "Not now".
 */
export async function mayCheckDevicePositionQuietly(): Promise<boolean> {
  return mayQuietlyRefreshLocation(await getGeolocationPermissionState(), readShownBrowseLocation());
}
